interface CognitoError extends Error {
  code: string;
}

export const isCognitoError = (err: Error | unknown): err is CognitoError => {
  return (err as CognitoError).code !== undefined;
};

export enum CognitoErrors {
  /** This exception is thrown when Amazon Cognito encounters an internal error. */
  InternalErrorException = 'InternalErrorException',
  /** This exception is thrown when the Amazon Cognito service encounters an invalid parameter. */
  InvalidParameterException = 'InvalidParameterException',
  /** This exception is thrown when a user is not authorized. */
  NotAuthorizedException = 'NotAuthorizedException',
  /** This exception is thrown when a password reset is required. */
  PasswordResetRequiredException = 'PasswordResetRequiredException',
  /** This exception is thrown when the Amazon Cognito service cannot find the requested resource. */
  ResourceNotFoundException = 'ResourceNotFoundException',
  /** This exception is thrown when the user has made too many requests for a given operation. */
  TooManyRequestsException = 'TooManyRequestsException',
  /** This exception is thrown when a user is not confirmed successfully. */
  UserNotConfirmedException = 'UserNotConfirmedException',
  /** This exception is thrown when a user is not found. */
  UserNotFoundException = 'UserNotFoundException',
  /** This exception is thrown when the Amazon Cognito service encounters an invalid AWS Lambda response. */
  InvalidLambdaResponseException = 'InvalidLambdaResponseException',
  /** This exception is returned when the role provided for SMS configuration does not have permission to publish using Amazon SNS. */
  InvalidSmsRoleAccessPolicyException = 'InvalidSmsRoleAccessPolicyException',
  /** This exception is thrown when the trust relationship is invalid for the role provided for SMS configuration. This can happen if you do not trust cognito-idp.amazonaws.com or the external ID provided in the role does not match what is provided in the SMS configuration for the user pool. */
  InvalidSmsRoleTrustRelationshipException = 'InvalidSmsRoleTrustRelationshipException',
  /** This exception is thrown when the user pool configuration is invalid. */
  InvalidUserPoolConfigurationException = 'InvalidUserPoolConfigurationException',
  /** This exception is thrown when the Amazon Cognito service encounters an unexpected exception with the AWS Lambda service. */
  UnexpectedLambdaException = 'UnexpectedLambdaException',
  /** This exception is thrown when the Amazon Cognito service encounters a user validation exception with the AWS Lambda service. */
  UserLambdaValidationException = 'UserLambdaValidationException',
  /* This exception is thrown when a user tries to confirm the account with an email or phone number that has already been supplied as an alias from a different account. This exception tells user that an account with this email or phone already exists. */
  AliasExistsException = 'AliasExistsException',
  /* This exception is thrown if the provided code does not match what the server was expecting. */
  CodeMismatchException = 'CodeMismatchException',
  /* This exception is thrown if a code has expired. */
  ExpiredCodeException = 'ExpiredCodeException',
  /* This exception is thrown when the Amazon Cognito service encounters an invalid password. */
  InvalidPasswordException = 'InvalidPasswordException',
  /* This exception is thrown when Amazon Cognito cannot find a multi-factor authentication (MFA) method. */
  MFAMethodNotFoundException = 'MFAMethodNotFoundException',
  /* This exception is thrown when the software token TOTP multi-factor authentication (MFA) is not enabled for the user pool. */
  SoftwareTokenMFANotFoundException = 'SoftwareTokenMFANotFoundException',
  /* This exception is thrown if two or more modifications are happening concurrently. */
  ConcurrentModificationException = 'ConcurrentModificationException',
  /* This exception is thrown when a user exceeds the limit for a requested AWS resource. */
  LimitExceededException = 'LimitExceededException',
  /* This exception is thrown when the user has made too many failed attempts for a given action (e.g., sign in). */
  TooManyFailedAttemptsException = 'TooManyFailedAttemptsException',
  /* This exception is thrown when a verification code fails to deliver successfully. */
  CodeDeliveryFailureException = 'CodeDeliveryFailureException',
  /* This exception is thrown when Amazon Cognito is not allowed to use your email identity. HTTP status code: 400. */
  InvalidEmailRoleAccessPolicyException = 'InvalidEmailRoleAccessPolicyException',
  /* This exception is thrown when there is a code mismatch and the service fails to configure the software token TOTP multi-factor authentication (MFA). */
  EnableSoftwareTokenMFAException = 'EnableSoftwareTokenMFAException',
}
